import {
  startRegistration,
  startAuthentication,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { endpoints } from '../api/client.js';
import type { EnrollResult, TwoFactorResult, WebAuthnRegisterResult } from '../api/types.js';

/**
 * WebAuthn ceremonies. The server emits an options envelope (`WebAuthnOptions`), @simplewebauthn/browser
 * runs the browser ceremony, the server verifies and stores the credential per `sso.UserMfaMethod`.
 * RP ID = `pinakes.be` (valid across all *.pinakes.be subdomains).
 *
 * The challenge is bound server-side via `challengeId`, echoed back on verify.
 */

/**
 * The opaque options envelope returned by the backend. `optionsJson` is a serialized FIDO2 options
 * JSON string (the Application layer is framework-free and never references Fido2 types).
 */
interface WebAuthnOptions {
  challengeId: string;
  optionsJson: string;
}

/** Passkey ENROLLMENT ceremony — navigator.credentials.create() (POST /api/mfa/passkey/*). */
export async function registerPasskey(
  displayName: string,
): Promise<EnrollResult | WebAuthnRegisterResult> {
  // 1. Server builds the registration options (challenge bound server-side via challengeId).
  const envelope = (await endpoints.mfaPasskeyOptions()) as WebAuthnOptions;
  const optionsJSON = JSON.parse(envelope.optionsJson) as PublicKeyCredentialCreationOptionsJSON;

  // 2. Browser ceremony — prompts the authenticator (Touch ID / Windows Hello / security key).
  const attResp = await startRegistration({ optionsJSON });

  // 3. Server verifies attestation and stores the credential.
  return endpoints.mfaPasskeyVerify({
    challengeId: envelope.challengeId,
    attestationResponseJson: JSON.stringify(attResp),
    displayName,
  });
}

/** Passkey LOGIN ceremony — navigator.credentials.get() (POST /api/login/passkey/*). */
export async function loginWithPasskey(): Promise<TwoFactorResult> {
  const envelope = (await endpoints.loginPasskeyOptions()) as WebAuthnOptions;
  const optionsJSON = JSON.parse(envelope.optionsJson) as PublicKeyCredentialRequestOptionsJSON;

  const asseResp = await startAuthentication({ optionsJSON });

  // Server checks the signature AND rejects sign-count regression (M6: cloned-authenticator signal).
  return endpoints.loginPasskeyVerify({
    challengeId: envelope.challengeId,
    assertionResponseJson: JSON.stringify(asseResp),
  });
}

/**
 * Progressive enhancement: hide passkey UI where the browser can't do WebAuthn.
 * TOTP always stays as the accessible fallback (D6) — never passkey-only.
 */
export const webauthnSupported = (): boolean => typeof window.PublicKeyCredential !== 'undefined';

/** Whether a platform authenticator (Touch ID / Windows Hello) is likely available. */
export async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!webauthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}
